import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { startBatchQuizGeneration } from '../services/tutorQuizService';
import TutorQuizReviewQueue from '../components/tutor/quiz/TutorQuizReviewQueue';
import TutorBatchJobProgress from '../components/tutor/quiz/TutorBatchJobProgress';
import TutorTopicGapsPanel from '../components/tutor/quiz/TutorTopicGapsPanel';

export default function TutorQuizReviewPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cId = Number(courseId);

  const [tab, setTab] = useState(0);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [quizzesPerTopic, setQuizzesPerTopic] = useState(1);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  async function handleStartGeneration() {
    setGenerating(true);
    try {
      const job = await startBatchQuizGeneration(cId, quizzesPerTopic);
      setActiveJobId(job.jobId!);
      setGenerateDialogOpen(false);
    } catch {
      /* error handled by service */
    } finally {
      setGenerating(false);
    }
  }

  const handleJobComplete = useCallback(() => {
    setActiveJobId(null);
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        <Typography color="text.primary">Quiz Review</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Quiz Review</Typography>
        <Button variant="contained" onClick={() => setGenerateDialogOpen(true)}>
          Generate Batch Quizzes
        </Button>
      </Box>

      {activeJobId && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>Generation in progress…</Typography>
          <TutorBatchJobProgress courseId={cId} jobId={activeJobId} onComplete={handleJobComplete} />
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Review Queue" />
        <Tab label="Topics Without Quizzes" />
      </Tabs>

      {tab === 0 && <TutorQuizReviewQueue key={refreshKey} courseId={cId} />}
      {tab === 1 && <TutorTopicGapsPanel courseId={cId} />}

      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Batch Quizzes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            AI will generate quizzes for all course topics and add them to this review queue.
          </Typography>
          <TextField
            label="Quizzes per topic"
            type="number"
            value={quizzesPerTopic}
            onChange={e => setQuizzesPerTopic(Math.max(1, Math.min(5, Number(e.target.value))))}
            inputProps={{ min: 1, max: 5 }}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>Cancel</Button>
          <Button variant="contained" onClick={handleStartGeneration} disabled={generating}>
            {generating ? 'Starting…' : 'Start Generation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
